import { useState } from 'react';
import { Button, Divider, ListSubheader, Menu, MenuItem } from '@mui/material';
import { useAIActions, type AiActionDto } from '../../api/queries/aiActions';
import { AIActionDialog } from './AIActionDialog';

interface AIToolbarButtonProps {
  paragraphId?: string | null;
  bookId?: string;
  chapterId?: string;
  onImageGenerate?: () => void;
  onAudioGenerate?: () => void;
  onTextGenerate?: () => void;
  onFindSimilar?: () => void;
}

const GENERATE_SLUGS = new Set([
  'image-generation',
  'audio-generation',
  'text-generation',
]);
const EDIT_SLUGS = new Set([
  'expand-paragraph',
  'rewrite-paragraph',
  'continue-writing',
  'summarize',
]);
const FIND_SLUGS = new Set(['find-similar']);

function categorize(actions: AiActionDto[]) {
  const generate: AiActionDto[] = [];
  const edit: AiActionDto[] = [];
  const find: AiActionDto[] = [];
  const other: AiActionDto[] = [];

  for (const action of actions) {
    if (GENERATE_SLUGS.has(action.slug)) generate.push(action);
    else if (EDIT_SLUGS.has(action.slug)) edit.push(action);
    else if (FIND_SLUGS.has(action.slug)) find.push(action);
    else other.push(action);
  }
  return { generate, edit, find, other };
}

/**
 * "AI-Everywhere" entry point: a button + categorized dropdown of registry actions.
 * Generate actions open the GenerationModal in the appropriate mode.
 * Edit actions open the AIActionDialog with context params.
 * Find actions open the RelevantContextDrawer.
 */
export function AIToolbarButton({
  paragraphId,
  bookId,
  chapterId,
  onImageGenerate,
  onAudioGenerate,
  onTextGenerate,
  onFindSimilar,
}: AIToolbarButtonProps) {
  const actions = useAIActions();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  // AIActionDialog state for Edit actions
  const [dialogAction, setDialogAction] = useState<AiActionDto | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { generate, edit, find, other } = categorize(actions.data ?? []);

  const handleSelect = (action: AiActionDto) => {
    setAnchor(null);

    // Generate group
    if (action.slug === 'image-generation') {
      onImageGenerate?.();
      return;
    }
    if (action.slug === 'audio-generation') {
      onAudioGenerate?.();
      return;
    }
    if (action.slug === 'text-generation') {
      onTextGenerate?.();
      return;
    }

    // Find group
    if (action.slug === 'find-similar') {
      onFindSimilar?.();
      return;
    }

    // Edit group (and any uncategorized actions) -> open AIActionDialog
    setDialogAction(action);
    setDialogOpen(true);
  };

  const contextParams: Record<string, unknown> = {};
  if (paragraphId) contextParams.paragraph_id = paragraphId;
  if (bookId) contextParams.book_id = bookId;
  if (chapterId) contextParams.chapter_id = chapterId;

  const renderSection = (
    label: string,
    items: AiActionDto[],
    showDivider: boolean,
  ) => {
    if (items.length === 0) return null;
    return [
      showDivider && <Divider key={`${label}-divider`} />,
      <ListSubheader key={`${label}-header`} disableSticky>
        {label}
      </ListSubheader>,
      ...items.map((action) => (
        <MenuItem key={action.id} onClick={() => handleSelect(action)}>
          {action.name}
        </MenuItem>
      )),
    ];
  };

  // Track whether we've rendered a section already, for divider logic
  const hasGenerate = generate.length > 0;
  const hasEdit = edit.length > 0;
  const hasFind = find.length > 0;

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        AI
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
      >
        {renderSection('Generate', generate, false)}
        {renderSection('Edit', edit, hasGenerate)}
        {renderSection('Find', find, hasGenerate || hasEdit)}
        {renderSection('Other', other, hasGenerate || hasEdit || hasFind)}
      </Menu>
      <AIActionDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setDialogAction(null);
        }}
        action={dialogAction}
        contextParams={contextParams}
      />
    </>
  );
}
